import { useAuth } from "src/hooks/useAuth";
import { useEffect, useReducer } from "react";
import type { QueryRecommendationsResult } from "src/types/adapters/query.type";
import { createReducer, type ReducerAction } from "src/utils/create-reducer";
import { useShopTypeContext } from "../context/use-shop-type-context";
import { useSearchProvider } from "../use-search-provider";

type QueryRecommendationsState = {
  loading: boolean;
  results: QueryRecommendationsResult | undefined;
  error: boolean;
};

type SearchActions =
  | ReducerAction<"SET_RESULT", QueryRecommendationsResult>
  | ReducerAction<"SET_LOADING", boolean>
  | ReducerAction<"SET_ERROR", boolean>;

const searchReducer = createReducer<QueryRecommendationsState, SearchActions>({
  SET_LOADING(state, payload) {
    return { ...state, loading: payload };
  },
  SET_RESULT(state, payload) {
    return { ...state, results: payload, loading: false };
  },
  SET_ERROR(state, payload) {
    return { ...state, error: payload, loading: false };
  },
});

const INITIAL_STATE = {
  loading: false,
  results: undefined,
  error: false,
} satisfies QueryRecommendationsState;

function useQueryRecommendations() {
  const { user } = useAuth();
  const { currentShopType } = useShopTypeContext();

  const [state, dispatch] = useReducer(searchReducer, INITIAL_STATE);

  const [searchProvider, isReady] = useSearchProvider();

  useEffect(() => {
    dispatch({ type: "SET_LOADING", payload: true });

    if (!isReady || !searchProvider) {
      return;
    }

    searchProvider
      .queryRecommendations({
        gender: currentShopType,
        isLoggedIn: Boolean(user?.member_id),
      })
      .then((results) => dispatch({ type: "SET_RESULT", payload: results }))
      .catch((error) => {
        console.error("[Search] An error occurred:", error);
        dispatch({ type: "SET_ERROR", payload: true });
      });
  }, [currentShopType, searchProvider, user?.member_id]);

  return state;
}

export { useQueryRecommendations };
